import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, PawPrint, ShieldCheck } from "lucide-react";
import { api, apiError, money } from "@/lib/helpers";
import { toast } from "sonner";

const PRESETS = [25, 50, 100, 250];

export default function DonateQuick() {
  const navigate = useNavigate();
  const [amount, setAmount] = useState(25);
  const [custom, setCustom] = useState("");
  const [monthly, setMonthly] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const give = async () => {
    const final = custom ? parseInt(custom, 10) || 0 : amount;
    if (!name.trim() || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Add your name and a valid email for your receipt.");
      return;
    }
    if (final < 1) { toast.error("Choose an amount first."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        kind: "donation", amount: final, monthly, name, email, origin_url: window.location.origin,
      });
      toast.success("Taking you to secure Stripe checkout…");
      setTimeout(() => { window.location.href = data.checkout_url; }, 600);
    } catch (e) {
      toast.error(apiError(e));
      setBusy(false);
    }
  };

  return (
    <main className="bg-sand">
      <div className="relative min-h-screen overflow-hidden grain">
        <img src="/sections/hero_sunset.webp" alt="A dog overlooking the valley at sunset" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/60 via-charcoal/45 to-pine-deep/90" />

        <div className="relative z-10 mx-auto flex min-h-screen max-w-lg flex-col justify-center px-4 py-16">
          <motion.button data-testid="quick-back-home" onClick={() => navigate("/")}
            className="mb-6 flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-sand/70 hover:text-gold-soft transition-colors"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <PawPrint className="h-4 w-4" /> Wag n Tails Mountain Retreat Rescue
          </motion.button>

          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="rounded-[2rem] bg-sand p-6 sm:p-10 shadow-2xl" data-testid="quick-donate-card">
            <Heart className="h-8 w-8 text-gold" />
            <h1 className="mt-4 font-serif text-3xl text-charcoal leading-tight">Feed a rescue today.</h1>
            <p className="mt-2 text-sm text-bark">100% of your gift goes to the animals — food, vaccines, warm beds and care.</p>

            <div className="mt-6 flex flex-wrap gap-2.5">
              {PRESETS.map((a) => (
                <button key={a} data-testid={`quick-amount-${a}`} onClick={() => { setAmount(a); setCustom(""); }}
                  className={`rounded-full px-5 py-2.5 font-mono text-sm font-semibold transition-all ${
                    !custom && amount === a ? "bg-pine text-sand scale-105" : "bg-tinted text-bark border border-stone-300"
                  }`}>
                  ${a}
                </button>
              ))}
              <input data-testid="quick-custom-amount" type="number" min="1" placeholder="Other $" value={custom}
                onChange={(e) => setCustom(e.target.value)}
                className="w-24 rounded-full border border-stone-300 bg-white px-4 py-2.5 font-mono text-sm outline-none focus:ring-2 focus:ring-gold" />
            </div>

            <label data-testid="quick-monthly-toggle" className="mt-4 inline-flex cursor-pointer items-center gap-3 text-sm text-bark">
              <button role="switch" aria-checked={monthly} data-testid="quick-monthly-switch" onClick={() => setMonthly(!monthly)}
                className={`relative h-6 w-11 rounded-full transition-colors ${monthly ? "bg-gold" : "bg-stone-300"}`}>
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${monthly ? "left-[22px]" : "left-0.5"}`} />
              </button>
              Make it monthly
            </label>

            <div className="mt-5 grid gap-3">
              <input data-testid="quick-name-input" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)}
                className="rounded-xl border border-stone-300 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-gold" />
              <input data-testid="quick-email-input" type="email" placeholder="Email for your receipt" value={email} onChange={(e) => setEmail(e.target.value)}
                className="rounded-xl border border-stone-300 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-gold" />
            </div>

            <button data-testid="quick-give-btn" onClick={give} disabled={busy}
              className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-gold py-4 font-bold text-charcoal transition-all hover:bg-gold-soft hover:scale-[1.01] active:scale-95 disabled:opacity-60">
              <Heart className="h-5 w-5" />
              {busy ? "Opening secure checkout…" : `Give ${money(custom ? parseInt(custom, 10) || 0 : amount)}${monthly ? " / month" : ""}`}
            </button>
            <p className="mt-3 flex items-center justify-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-bark/60">
              <ShieldCheck className="h-3.5 w-3.5" /> Secure checkout powered by Stripe
            </p>
          </motion.div>
        </div>
      </div>
    </main>
  );
}
